import { useState, type FormEvent, type ReactNode } from 'react';
import { passwordSchema } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { Alert, Button, Field } from '@/components/ui';

export interface DetailsFormState {
  readonly churchName: string;
  readonly slug: string;
  /** Once true, typing in `churchName` no longer overwrites `slug`. */
  readonly slugEdited: boolean;
  readonly ownerName: string;
  readonly email: string;
  readonly password: string;
}

/** Mirrors `tenantSlugSchema`: lowercase, hyphenated words, no other punctuation. */
function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function DetailsStep({
  value,
  onChange,
  onNext,
  onBack,
  serverFieldErrors,
  formError,
}: {
  readonly value: DetailsFormState;
  readonly onChange: (patch: Partial<DetailsFormState>) => void;
  readonly onNext: () => void;
  readonly onBack: () => void;
  /** Field-keyed errors the server already reported (e.g. a taken slug). */
  readonly serverFieldErrors: Record<string, string>;
  /** A failed submission's reason that isn't already covered by a field above. */
  readonly formError: string | null;
}): ReactNode {
  const { t } = useI18n();
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleChurchNameChange(nextName: string): void {
    onChange({
      churchName: nextName,
      ...(value.slugEdited ? {} : { slug: slugify(nextName) }),
    });
  }

  function handleSlugChange(nextSlug: string): void {
    onChange({ slug: slugify(nextSlug), slugEdited: true });
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!value.churchName.trim()) next.churchName = t('common.labels.required');
    if (value.slug.length < 3 || !SLUG_PATTERN.test(value.slug)) {
      next.slug = t('common.labels.required');
    }
    if (!value.ownerName.trim()) next.ownerName = t('common.labels.required');
    if (!value.email.trim() || !value.email.includes('@')) next.email = t('common.labels.required');
    // Delegates to the real policy rather than a second hand-copied number —
    // a duplicated `12` here is exactly what drifted out of sync the last
    // time the server-side minimum changed.
    if (!passwordSchema.safeParse(value.password).success) {
      next.password = t('registration.details.passwordTooShort');
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (validate()) onNext();
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="auth-form">
      <h2 className="auth-title">{t('registration.details.title')}</h2>

      {formError && Object.keys(serverFieldErrors).length === 0 && (
        <Alert tone="danger">{formError}</Alert>
      )}

      <Field
        label={t('registration.details.churchName')}
        name="churchName"
        value={value.churchName}
        onChange={(event) => handleChurchNameChange(event.target.value)}
        error={errors.churchName ?? serverFieldErrors.churchName}
        required
        autoFocus
      />

      <Field
        label={t('registration.details.slug')}
        name="slug"
        value={value.slug}
        onChange={(event) => handleSlugChange(event.target.value)}
        error={errors.slug ?? serverFieldErrors.slug}
        hint={t('registration.details.slugHint')}
        required
        dir="ltr"
      />

      <Field
        label={t('registration.details.ownerName')}
        name="ownerName"
        value={value.ownerName}
        onChange={(event) => onChange({ ownerName: event.target.value })}
        error={errors.ownerName ?? serverFieldErrors.ownerName}
        required
      />

      <Field
        label={t('registration.details.email')}
        name="email"
        type="email"
        value={value.email}
        onChange={(event) => onChange({ email: event.target.value })}
        error={errors.email ?? serverFieldErrors.email}
        required
        dir="ltr"
      />

      <Field
        label={t('registration.details.password')}
        name="password"
        type="password"
        value={value.password}
        onChange={(event) => onChange({ password: event.target.value })}
        error={errors.password ?? serverFieldErrors.password}
        hint={t('registration.details.passwordHint')}
        required
        dir="ltr"
      />

      <div className="card-fields-row">
        <Button type="button" variant="ghost" onClick={onBack}>
          {t('common.actions.back')}
        </Button>
        <Button type="submit" variant="primary">
          {t('registration.details.continue')}
        </Button>
      </div>
    </form>
  );
}
